import React, { useEffect, useState, useCallback } from "react";
import { api, API, errMsg, fmtMoney, fmtNum, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Factory, LogOut, LayoutDashboard, FileText, FileSignature, ReceiptText,
  Banknote, User, Loader2, Upload, Send,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "spas", label: "My Agreements", icon: FileText },
  { key: "quote", label: "Submit Quotation", icon: FileSignature },
  { key: "invoices", label: "My Invoices", icon: ReceiptText },
  { key: "payments", label: "Payment Status", icon: Banknote },
  { key: "profile", label: "Profile", icon: User },
];

const Card = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`bg-white border border-slate-200 rounded-lg ${className}`}>{children}</div>
);

function Overview({ summary, go }) {
  if (!summary) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const s = summary.supplier || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Active Agreements", value: fmtNum(summary.active_spas), tone: "text-[#12346B]" },
          { label: "Open Invoices", value: fmtNum(summary.open_invoices), tone: "text-orange-600" },
          { label: "Outstanding", value: fmtMoney(summary.outstanding), tone: "text-slate-800" },
          { label: "Paid To Date", value: fmtMoney(summary.paid_total), tone: "text-emerald-600" },
        ].map((k) => (
          <Card key={k.label} className="p-4" data-testid={`sp-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className={`font-head text-2xl font-bold mt-1 tabular ${k.tone}`}>{k.value}</div>
          </Card>
        ))}
      </div>
      <Card className="p-6">
        <h3 className="font-head font-semibold text-slate-800 mb-1">Welcome, {s.name}</h3>
        <p className="text-sm text-slate-500">Supplier code <b className="mono">{s.code}</b> · Risk rating {s.risk_rating} · <StatusBadge status={s.status} /></p>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button onClick={() => go("quote")} className="bg-[#12346B] hover:bg-[#12346B]/90" data-testid="sp-cta-quote">
            <FileSignature className="h-4 w-4 mr-1.5" /> Submit a Quotation
          </Button>
          <Button variant="outline" onClick={() => go("invoices")}>Submit an Invoice</Button>
        </div>
      </Card>
    </div>
  );
}

function Agreements() {
  const [spas, setSpas] = useState(null);
  useEffect(() => { api.get("/supplier-portal/spas").then(({ data }) => setSpas(data)).catch(() => setSpas([])); }, []);
  if (!spas) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (!spas.length) return <Card className="py-16 text-center text-slate-400 text-sm">No agreements yet.</Card>;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="pf-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["SPA No.", "Product", "Contract Qty", "Transferred", "Unit $", "Total", "Status"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {spas.map((s) => (
              <TableRow key={s.id} className="border-slate-100" data-testid={`sp-spa-${s.id}`}>
                <TableCell className="py-2 px-3 mono text-xs font-semibold">{s.spa_no}</TableCell>
                <TableCell className="py-2 px-3 text-[13px]">{s.product_name}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtNum(s.quantity)}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtNum(s.transferred_qty)}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtMoney(s.unit_price)}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtMoney(s.total_value)}</TableCell>
                <TableCell className="py-2 px-3"><StatusBadge status={s.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function Quote({ reload }) {
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/supplier-portal/quotations").then(({ data }) => setQuotes(data)).catch(() => {}), []);
  useEffect(() => { api.get("/supplier-portal/products").then(({ data }) => setProducts(data)).catch(() => {}); load(); }, [load]);

  const submit = async () => {
    if (!f.product_id || !f.quantity || !f.unit_price) { toast.error("Product, quantity and price are required"); return; }
    setBusy(true);
    try {
      await api.post("/supplier-portal/quotations", { ...f, quantity: Number(f.quantity), unit_price: Number(f.unit_price) });
      toast.success("Quotation submitted to PCI procurement");
      setF({}); load(); reload && reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-6">
        <h3 className="font-head font-semibold text-slate-800 mb-4">New Quotation</h3>
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-slate-600">Product</Label>
            <Select value={f.product_id || ""} onValueChange={(v) => setF((s) => ({ ...s, product_id: v }))}>
              <SelectTrigger data-testid="sp-quote-product"><SelectValue placeholder="Select product…" /></SelectTrigger>
              <SelectContent className="bg-white z-[60]">
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Quantity (L)</Label>
              <Input type="number" data-testid="sp-quote-qty" value={f.quantity || ""} onChange={(e) => setF((s) => ({ ...s, quantity: e.target.value }))} /></div>
            <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Unit Price (USD)</Label>
              <Input type="number" data-testid="sp-quote-price" value={f.unit_price || ""} onChange={(e) => setF((s) => ({ ...s, unit_price: e.target.value }))} /></div>
          </div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Valid Until</Label>
            <Input type="date" data-testid="sp-quote-valid" value={f.valid_until || ""} onChange={(e) => setF((s) => ({ ...s, valid_until: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Notes</Label>
            <Textarea data-testid="sp-quote-notes" value={f.notes || ""} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))} /></div>
          <Button onClick={submit} disabled={busy} className="w-full bg-[#12346B] hover:bg-[#12346B]/90" data-testid="sp-quote-submit">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}<Send className="h-4 w-4 mr-1.5" /> Submit Quotation
          </Button>
        </div>
      </Card>
      <Card className="p-4">
        <h3 className="font-head font-semibold text-slate-800 mb-3 px-2">Submitted Quotations</h3>
        {quotes.length === 0 ? <p className="text-sm text-slate-400 px-2 py-8 text-center">None yet.</p> : (
          <div className="space-y-2">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center justify-between border border-slate-100 rounded-md px-3 py-2" data-testid={`sp-quote-${q.id}`}>
                <div>
                  <div className="mono text-xs font-semibold">{q.quotation_no}</div>
                  <div className="text-xs text-slate-500">{q.product_name} · {fmtNum(q.quantity)} L @ {fmtMoney(q.unit_price)}</div>
                </div>
                <div className="text-right"><div className="tabular text-sm font-semibold">{fmtMoney(q.total_value)}</div><StatusBadge status={q.status} /></div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Invoices({ reload }) {
  const [invoices, setInvoices] = useState(null);
  const [spas, setSpas] = useState([]);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const load = useCallback(() => api.get("/supplier-portal/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([])), []);
  useEffect(() => {
    load();
    api.get("/supplier-portal/spas").then(({ data }) => setSpas(data.filter((s) => ["Active", "Partially Fulfilled"].includes(s.status)))).catch(() => {});
  }, [load]);

  const submit = async () => {
    if (!f.spa_id || !f.invoice_no || !f.quantity || !f.unit_price) { toast.error("SPA, invoice ref, quantity and price required"); return; }
    setBusy(true);
    try {
      await api.post("/supplier-portal/invoices", { ...f, quantity: Number(f.quantity), unit_price: Number(f.unit_price), taxes: Number(f.taxes || 0), fees: Number(f.fees || 0) });
      toast.success("Invoice submitted — PCI finance will verify");
      setF({}); load(); reload && reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const upload = async (inv, file) => {
    if (!file) return;
    setUploading(inv.id);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/supplier-portal/invoices/${inv.id}/evidence`, fd); toast.success("Evidence uploaded"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(null); }
  };

  if (!invoices) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="font-head font-semibold text-slate-800 mb-4">Submit Invoice against Agreement</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="grid gap-1.5 md:col-span-3"><Label className="text-xs font-medium text-slate-600">Agreement (SPA)</Label>
            <Select value={f.spa_id || ""} onValueChange={(v) => setF((s) => ({ ...s, spa_id: v }))}>
              <SelectTrigger data-testid="sp-inv-spa"><SelectValue placeholder="Select active agreement…" /></SelectTrigger>
              <SelectContent className="bg-white z-[60]">
                {spas.map((s) => <SelectItem key={s.id} value={s.id}>{s.spa_no} · {s.product_name} · {fmtNum(s.quantity - s.transferred_qty)} L remaining</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Invoice Ref</Label>
            <Input data-testid="sp-inv-ref" value={f.invoice_no || ""} onChange={(e) => setF((s) => ({ ...s, invoice_no: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Quantity (L)</Label>
            <Input type="number" data-testid="sp-inv-qty" value={f.quantity || ""} onChange={(e) => setF((s) => ({ ...s, quantity: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Unit Price (USD)</Label>
            <Input type="number" data-testid="sp-inv-price" value={f.unit_price || ""} onChange={(e) => setF((s) => ({ ...s, unit_price: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Taxes</Label>
            <Input type="number" value={f.taxes || ""} onChange={(e) => setF((s) => ({ ...s, taxes: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Fees</Label>
            <Input type="number" value={f.fees || ""} onChange={(e) => setF((s) => ({ ...s, fees: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">Due Date</Label>
            <Input type="date" value={f.due_date || ""} onChange={(e) => setF((s) => ({ ...s, due_date: e.target.value }))} /></div>
        </div>
        <Button onClick={submit} disabled={busy} className="mt-4 bg-[#12346B] hover:bg-[#12346B]/90" data-testid="sp-inv-submit">
          {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Submit Invoice
        </Button>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="pf-table">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Invoice", "Ref", "SPA", "Qty", "Total", "3-Way Match", "Status", "Evidence"].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-400 text-sm">No invoices submitted yet.</TableCell></TableRow>
              ) : invoices.map((i) => (
                <TableRow key={i.id} className="border-slate-100" data-testid={`sp-invoice-${i.id}`}>
                  <TableCell className="py-2 px-3 mono text-xs font-semibold">{i.invoice_no}</TableCell>
                  <TableCell className="py-2 px-3 mono text-xs">{i.supplier_ref}</TableCell>
                  <TableCell className="py-2 px-3 mono text-xs">{i.spa_no}</TableCell>
                  <TableCell className="py-2 px-3 tabular">{fmtNum(i.quantity)}</TableCell>
                  <TableCell className="py-2 px-3 tabular">{fmtMoney(i.total)}</TableCell>
                  <TableCell className="py-2 px-3 text-xs">{i.match_exceptions?.length ? <span className="text-rose-600">{i.match_exceptions.length} exception(s)</span> : <span className="text-emerald-600">Matched</span>}</TableCell>
                  <TableCell className="py-2 px-3"><StatusBadge status={i.status} /></TableCell>
                  <TableCell className="py-2 px-3">
                    {i.evidence_document_id ? (
                      <a href={`${API}/supplier-portal/documents/${i.evidence_document_id}`} target="_blank" rel="noreferrer" className="text-xs text-[#12346B] hover:underline">View</a>
                    ) : (
                      <label className="inline-flex items-center gap-1 text-xs text-[#12346B] cursor-pointer hover:underline" data-testid={`sp-evidence-${i.id}`}>
                        {uploading === i.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                        <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => upload(i, e.target.files[0])} />
                      </label>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function Payments() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/supplier-portal/payments").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (!rows.length) return <Card className="py-16 text-center text-slate-400 text-sm">No payments recorded yet.</Card>;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="pf-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Payment No.", "Invoice", "Amount", "Method", "Reference", "Status"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} className="border-slate-100" data-testid={`sp-payment-${p.id}`}>
                <TableCell className="py-2 px-3 mono text-xs font-semibold">{p.payment_no}</TableCell>
                <TableCell className="py-2 px-3 mono text-xs">{p.invoice_no}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtMoney(p.amount)}</TableCell>
                <TableCell className="py-2 px-3 text-xs">{p.method}</TableCell>
                <TableCell className="py-2 px-3 mono text-xs">{p.reference}</TableCell>
                <TableCell className="py-2 px-3"><StatusBadge status={p.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function Profile({ summary }) {
  const s = summary?.supplier || {};
  const rows = [["Code", s.code], ["Legal Name", s.name], ["Classification", s.classification], ["Country", s.country],
    ["Email", s.email], ["Phone", s.phone], ["Tax ID", s.tax_id], ["License No.", s.license_no],
    ["License Expiry", s.license_expiry], ["Bank", s.bank_name], ["Risk Rating", s.risk_rating], ["Status", s.status]];
  return (
    <Card className="p-6 max-w-xl">
      <h3 className="font-head font-semibold text-slate-800 mb-4">Company Profile</h3>
      <div className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between py-2 text-sm">
            <span className="text-slate-500">{k}</span>
            <span className="font-medium text-slate-800">{v || "—"}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-4">To update banking or licensing details, contact your PCI procurement officer (high-risk change control applies).</p>
    </Card>
  );
}

export default function SupplierPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const reload = useCallback(() => api.get("/supplier-portal/summary").then(({ data }) => setSummary(data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-[#12346B] text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center overflow-hidden"><img src="/prime-logo.jpg" alt="PCI" className="h-9 w-9 object-cover" /></div>
          <div className="leading-tight">
            <div className="font-head font-extrabold text-sm">PCI PetroFlow · Supplier Portal</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Prime Contractors, Inc.</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-slate-200 hidden sm:block">{user?.name}</span>
            <button onClick={logout} data-testid="sp-logout" className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center" title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sp-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] border-b-2 whitespace-nowrap transition-colors ${
                tab === t.key ? "border-orange-500 text-white font-semibold" : "border-transparent text-slate-300 hover:text-white"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-6">
        <h1 className="font-head text-xl font-bold text-slate-900 mb-4">{TABS.find((t) => t.key === tab)?.label}</h1>
        {tab === "overview" && <Overview summary={summary} go={setTab} />}
        {tab === "spas" && <Agreements />}
        {tab === "quote" && <Quote reload={reload} />}
        {tab === "invoices" && <Invoices reload={reload} />}
        {tab === "payments" && <Payments />}
        {tab === "profile" && <Profile summary={summary} />}
      </main>
      <footer className="text-center text-[11px] text-slate-400 py-3">PCI PetroFlow ERP™ · Secure Supplier Portal · Prime Contractors, Inc.</footer>
    </div>
  );
}
